/* eslint-disable no-debugger */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import { InputProps, useDataProvider, useInput } from 'react-admin';
import debounce from 'lodash/debounce';

type AutoCompleteReferenceProps = {
  reference: string;
  label: string;
  filter: any;
  sort: any;
  onChange?: any;
  isEdit?: boolean;
  parseLabel?: (item: any) => string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  perPage?: number;
  skip?: boolean;
} & InputProps;

export const AutoCompleteReference = ({
  reference,
  label,
  filter = {},
  sort = {},
  error,
  helperText,
  onChange = null,
  parseLabel,
  disabled = false,
  perPage = 10,
  skip = false,
  value,
  ...props
}: AutoCompleteReferenceProps) => {
  const { input } = useInput(props);
  const dataProvider = useDataProvider();
  const currentRequestId = useRef(0);

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [referenceData, setReferenceData] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [actualItem, setActualItem] = useState<any>();

  const handleChange = async (_event, value, _reason, _details) => {
    if (onChange) onChange(value);
    input.onChange(value?.id ?? null);
    setSelected(value);
  };

  const getReferenceData = async (requestId: number, name: string) => {
    if (skip) return;
    setLoading(true);
    const mergedFilter = { ...filter };
    if (name) {
      mergedFilter.name = name;
    }
    let sortItems;
    if (sort) {
      sortItems = sort;
    } else {
      sortItems = { field: 'id', order: 'ASC' };
    }
    try {
      const referenceDataResponse = await dataProvider.getList(reference, {
        pagination: { page: 1, perPage },
        filter: mergedFilter,
        sort: sortItems,
      });
      if (
        referenceDataResponse.data &&
        requestId === currentRequestId.current
      ) {
        if (actualItem) {
          const unique = Array.from(
            new Map(
              [...referenceDataResponse.data, actualItem].map(item => [
                item.id,
                item,
              ]),
            ).values(),
          );
          setReferenceData(unique);
        } else {
          setReferenceData(referenceDataResponse.data);
        }
      }

      setLoading(false);
    } catch (err) {
      console.log('err', err);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(name => {
        currentRequestId.current += 1;
        const requestId = currentRequestId.current;
        getReferenceData(requestId, name);
      }, 300),
    [dataProvider, JSON.stringify(filter), skip],
  );

  useEffect(() => {
    input.onChange(null);
    setSelected(null);
  }, []);

  // Reset internal state when skip becomes true (parent dependency cleared)
  useEffect(() => {
    if (skip) {
      setSelected(null);
      setActualItem(undefined);
      setReferenceData([]);
      setInputValue('');
      input.onChange(null);
    }
  }, [skip]);

  useEffect(() => {
    debouncedSearch(inputValue);

    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch]);

  const refreshActualItem = async (itemValue: string) => {
    if (skip) return;
    const referenceDataResponse = await dataProvider.getList(reference, {
      pagination: { page: 1, perPage },
      filter: {
        id: itemValue,
      },
      sort: { field: 'id', order: 'ASC' },
    });
    if (referenceDataResponse.data && referenceDataResponse.data.length > 0) {
      setActualItem(referenceDataResponse.data[0]);
      setSelected({
        id: referenceDataResponse.data[0].id,
        name: referenceDataResponse.data[0].name,
      });
    }
  };

  useEffect(() => {
    if (!value) return;
    const defaultItem = referenceData.find(item => item.id === value);
    if (!defaultItem) {
      refreshActualItem(value);
    } else if (selected?.id !== defaultItem.id) {
      // The parent can change `value` externally (e.g. the SupplyPro mapping
      // section picks another model), so mirror it into the local selection.
      setActualItem(defaultItem);
      setSelected({ id: defaultItem.id, name: defaultItem.name });
    }
  }, [value, referenceData]);

  useEffect(() => {
    if (value && !selected && !inputValue && label === 'Model') {
      input.onChange(value);
    }
    if (!value && label === 'Model') {
      input.onChange(null);
      setSelected(null);
      setActualItem(null);
    }
    if (!value && label === 'Builder') {
      input.onChange(null);
      setSelected(null);
      setActualItem(null);
    }
    if (!value && label === 'Subdivision') {
      input.onChange(null);
      setSelected(null);
      setActualItem(null);
    }
  }, [value, selected, inputValue]);

  return (
    <Autocomplete
      className="AutoCompleteReference"
      style={{ marginTop: '-8px' }}
      fullWidth
      getOptionSelected={(option, value) => option.id === value.id}
      getOptionLabel={option => (option ? option?.name : '')}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          name={props.resource}
          label={label}
          style={{ backgroundColor: 'transparent !important' }}
          margin="dense"
          variant="filled"
          error={error}
          helperText={helperText}
        />
      )}
      loading={loading}
      options={referenceData?.map(item => ({
        ...item,
        name: parseLabel ? parseLabel(item) : item.name,
        id: item.id,
      }))}
      {...input}
      value={selected}
      onChange={handleChange}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason !== 'reset') {
          setInputValue(newInputValue);
        }
      }}
    />
  );
};
